import { PdsWidget } from '../pds-widget';

export class PropertyObserver {

  public path: string;
  private host: any;
  private property: string;
  private propertyDescriptor: PropertyDescriptor | undefined;

  private value: any;

  private enabled = false;

  private widget: PdsWidget;

  private originalArray: {
    push: (...items: Array<any>) => number;
    pop: () => any;
    unshift: (...items: Array<any>) => number;
    shift: () => any;
    splice: (index: number, removeCount: number, ...items: Array<any>) => Array<any>
  };

  constructor(widget: PdsWidget, path: string) {
    this.widget = widget;
    this.path = path;
  }

  enable(): void {
    // already enabled, no need to continue
    if (this.enabled) {
      return;
    }

    // split to determine each step down the object graph
    let properties = this.path.split('.');

    // ensure this is actually a sub property
    if (properties.length <= 1 || properties[1].length === 0) {
      throw new Error(`Path needs to be at least one level deep: ${this.path}`);
    }

    // starting from the widget itself, traverse the object graph to the second to last step
    this.host = this.widget;
    for (let i = 0; i < properties.length - 1 && this.host != null; i++) {
      let property = properties[i];
      this.host = this.host[property];
    }

    // if any step is undefined, we can't set up the observer
    if (this.host == null) {
      throw new Error(`Path was not valid: ${this.path}`);
    }

    // get the last property
    this.property = properties[properties.length - 1];

    // get the initial value from the property on the host
    this.value = this.host[this.property];

    // keep track of the original property's settings
    this.propertyDescriptor = Object.getOwnPropertyDescriptor(this.host, this.property);

    // setting up an observer for a computed property won't work right
    if (this.propertyDescriptor && (this.propertyDescriptor.get || this.propertyDescriptor.set)) {
      throw new Error(`Cannot apply a property observable to a property with a getter or setter: ${this.path}`);
    }

    // create the property observer wrapper
    Object.defineProperty(this.host, this.property, {
      enumerable: true,
      configurable: true,
      // value is tracked in this observer
      get: () => {
        return this.value;
      },
      set: (value: any) => {
        if (this.value !== value) {
          // make sure we're not leaving array methods replaced
          this.unwrapArray(this.value);

          this.value = value;

          // wrap the new array, if necessary
          this.wrapArray(value);

          // notify the widget so polymer knows the value changed
          this.widget.notifyPath(this.path);
        }
      }
    });

    this.wrapArray(this.value);

    this.enabled = true;
  }

  disable(): void {
    // already disabled, no need to continue
    if (!this.enabled) {
      return;
    }

    // recreate the original property as best we can
    Object.defineProperty(this.host, this.property, this.propertyDescriptor || { value: this.value, writable: true });
    //Sometimes this.value will have changed and the value in this.propertyDescriptor will not have changed.
    //We need to update the value so it will be current.
    if (this.propertyDescriptor != null && this.propertyDescriptor.writable) {
      this.host[this.property] = this.value;
    }

    this.unwrapArray(this.value);

    this.enabled = false;
  }

  private unwrapArray(array: Array<any>): void {
    // if this is an array and we have original functions for it, set the functions back to the original ones
    if (this.originalArray == null || array == null || !Array.isArray(array)) {
      return;
    }

    array.push = this.originalArray.push;
    array.pop = this.originalArray.pop;
    array.unshift = this.originalArray.unshift;
    array.shift = this.originalArray.shift;
    array.splice = this.originalArray.splice;

    // removed the cached original functions now that they've been reapplied
    delete this.originalArray;
  }

  // This will force any array mutations to also notify polymer of the splices.
  //  See https://polymer-library.polymer-project.org/1.0/docs/devguide/model-data#array-mutation
  //  Bind to 'path.splices' to force change detection to see updates.
  private wrapArray(array: Array<any>): void {
    if (array == null || !Array.isArray(array)) {
      return;
    }

    // keep track of the original functions so we can unwrap
    this.originalArray = {
      push: array.push,
      pop: array.pop,
      unshift: array.unshift,
      shift: array.shift,
      splice: array.splice
    };

    // replace array mutators with polymer array mutators
    array.push = (...items: Array<any>) => {
      let index = this.value.length;

      let results = this.originalArray.push.call(this.value, ...items);

      this.widget.notifySplices(this.path, [{
          index: index,
          addedCount: items.length,
          object: this.value,
          removed: [],
          type: 'splice'
        }]
      );

      return results;
    };

    array.pop = () => {
      let result = this.originalArray.pop.call(this.value);

      this.widget.notifySplices(this.path, [{
        index: this.value.length,
        addedCount: 0,
        object: this.value,
        removed: [result],
        type: 'splice'
      }]);

      return result;
    };

    array.unshift = (...items: Array<any>) => {
      let results = this.originalArray.unshift.call(this.value, ...items);

      this.widget.notifySplices(this.path, [{
        index: 0,
        addedCount: items.length,
        object: this.value,
        removed: [],
        type: 'splice'
      }]);

      return results;
    };

    array.shift = () => {
      let result = this.originalArray.shift.call(this.value);

      this.widget.notifySplices(this.path, [{
        index: 0,
        addedCount: 0,
        object: this.value,
        removed: [result],
        type: 'splice'
      }]);

      return result;
    };

    array.splice = (index: number, removeCount: number, ...items: Array<any>) => {
      let results = this.originalArray.splice.call(this.value, index, removeCount, ...items);

      this.widget.notifySplices(this.path, [{
        index: index,
        addedCount: items.length,
        object: this.value,
        removed: results,
        type: 'splice'
      }]);

      return results;
    };
  }
}
